import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Theme from '@amzn/meridian/theme';
import { ThemeProvider, useTheme } from '../context/ThemeContext';
import PageLayout from 'src/components/AppLayout';
import AMZLDSInputs from 'src/pages/AMZLDSInputs';
import ControlRoom from 'src/pages/ControlRoom';
import Configuration from 'src/pages/Configuration';
import CapacityManagement from 'src/pages/CapacityManagement';
import OpsMonitor from 'src/pages/OpsMonitor';
import NotFound from 'src/pages/NotFound';
import { AuthProvider } from 'src/context/AuthContext';
import { PermissionsProvider, usePermissions } from 'src/context/PermissionsContext';

/**
 * ProtectedRoute — wraps routes that require the Bindle Lock.
 * If the user doesn't have full access, redirects to /amzl-updates.
 */
const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
    const { hasFullAccess, loading } = usePermissions();

    if (loading) return null; // Don't flash content while checking permissions
    if (!hasFullAccess) return <Navigate to="/amzl-updates" replace />;
    return children;
};

const ThemedApp = () => {
    const { resolvedMode } = useTheme();
    return (
        <Theme mode={resolvedMode}>
            <BrowserRouter>
                <PageLayout>
                    <Routes>
                        {/* Public route — accessible to ALL authenticated users */}
                        <Route path="/" element={<AMZLDSInputs />} />
                        <Route path="/amzl-updates" element={<AMZLDSInputs />} />

                        {/* Protected routes — require Bindle Lock */}
                        <Route path="/control-room" element={<ProtectedRoute><ControlRoom /></ProtectedRoute>} />
                        <Route path="/configuration" element={<ProtectedRoute><Configuration /></ProtectedRoute>} />
                        <Route path="/d1-amzl-capacity" element={<ProtectedRoute><CapacityManagement key="d1-amzl" horizon="D-1" program="AMZL" /></ProtectedRoute>} />
                        <Route path="/d2-amzl-capacity" element={<ProtectedRoute><CapacityManagement key="d2-amzl" horizon="D-2" program="AMZL" /></ProtectedRoute>} />
                        <Route path="/ops-monitor" element={<ProtectedRoute><OpsMonitor /></ProtectedRoute>} />

                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </PageLayout>
            </BrowserRouter>
        </Theme>
    );
};

const App = () => (
    <ThemeProvider>
        <AuthProvider>
            <PermissionsProvider>
                <ThemedApp />
            </PermissionsProvider>
        </AuthProvider>
    </ThemeProvider>
);

export default App;
